'use client';

/**
 * Product Page Content Component
 * Shared content for clean Magento-style product pages.
 *
 * Matches Magento production design:
 * - Product info on LEFT, main image on RIGHT
 * - Breadcrumbs with > separators and orange links
 * - Stock status badge with shipping message
 * - Collapsible product options accordion
 * - Add to Cart + Talk to Expert buttons
 * - Images and Videos sections below with lightbox
 */
import { c as _c } from "react/compiler-runtime";
import { useState, useCallback, useEffect } from 'react';
import Image from '@/components/media/StorefrontImage';
import Link from 'next/link';
import { ProductDetailsTabs, type ProductDetailsSection } from '@/components/pages/ProductDetailsTabs';
import { ProductOptionsSelectorMagento } from '@/components/product/ProductOptionsSelectorMagento';
import { ProductGallery } from '@/components/product/ProductGallery';
import { InstallationCTA } from '@/components/product/InstallationCTA';
import { TierPriceTable } from '@/components/product/TierPriceTable';
import { ProductLightbox } from '@/components/product/ProductLightbox';
import type { LightboxItem } from '@/components/product/ProductLightbox';
import { RelatedProducts } from '@/components/product/RelatedProducts';
import { ProductOptionsVisual } from '@/components/product/ProductOptionsVisual';
import type { ProductCardProduct } from '@/components/product/ProductCard';
import { getProductStockStatus } from '@/lib/product-stock';
import { useAvailability } from '@/hooks/useAvailability';
import { usePartnerPrice } from '@/hooks/usePartnerPrice';

// Types
interface ProductImage {
  id: number;
  url: string;
  altText: string | null;
  position: number;
  role: string | null;
}
interface ProductVideo {
  id: number;
  url: string;
  title: string | null;
  thumbnailUrl: string | null;
  position: number;
}
interface ProductOptionValue {
  id: number;
  title: string;
  skuSuffix: string | null;
  priceModifier: string;
  priceType: string;
  sortOrder: number;
  isDefault: boolean;
}
interface ProductOption {
  id: number;
  title: string;
  type: string;
  isRequired: boolean;
  sortOrder: number;
  values: ProductOptionValue[];
}
interface Category {
  id: number;
  name: string;
  slug: string;
}
interface ProductAttributes {
  [key: string]: string | number | boolean | undefined;
}
interface Product {
  id: number;
  sku: string;
  name: string;
  nameHtml?: string | null;
  slug: string;
  basePrice: string;
  specialPrice: string | null;
  specialFrom: string | Date | null;
  specialTo: string | Date | null;
  msrp: string | null;
  status: string;
  visibility: string;
  isDropship?: boolean | null;
  isPreorder?: boolean | null;
  isSpecialOrder?: boolean | null;
  shortDescription: string | null;
  productType: string | null;
  manufacturer: string | null;
  weight: string | null;
  mainVideo: string | null;
  attributes: ProductAttributes;
  images: ProductImage[];
  videos?: ProductVideo[];
  options: ProductOption[];
  categories: Category[];
}
interface TierPriceRow {
  minQty: number;
  price: string;
  customerGroupId: number | null;
}
interface ProductPageContentProps {
  product: Product;
  tierPrices?: TierPriceRow[];
  relatedProducts?: ProductCardProduct[];
  optionProducts?: ProductCardProduct[];
  /** Description/specs/downloads/how-to tabs, prepared and sanitized on the server. */
  detailSections: ProductDetailsSection[];
}

/**
 * Extract short product name from full name
 */
function getShortProductName(fullName: string): string {
  const parts = fullName.split(' - ');
  return (parts[0] ?? fullName).trim();
}

/**
 * Parse the Magento `name_html` display override (`<h1>title</h1><h3>line</h3>...`,
 * kits carry multiple h3 lines). Mirrors the Magento theme: when name_html is
 * present it wins over the name-split heuristic. Returns null when the value
 * doesn't contain an <h1> so callers fall back to splitting `name`.
 */
function parseNameHtml(nameHtml: string): {
  title: string;
  subtitleLines: string[];
} | null {
  const stripTags = (html: string) => html.replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').replace(/&deg;/g, '°').replace(/&nbsp;/g, ' ').replace(/&quot;/g, '"').replace(/&#0?39;/g, "'").replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/\s+/g, ' ').trim();
  const h1Match = nameHtml.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i);
  if (!h1Match?.[1]) return null;
  const title = stripTags(h1Match[1]);
  if (!title) return null;
  const subtitleLines: string[] = [];
  for (const h3Match of nameHtml.matchAll(/<h3[^>]*>([\s\S]*?)<\/h3>/gi)) {
    const line = stripTags(h3Match[1] ?? '');
    if (line) subtitleLines.push(line);
  }
  return {
    title,
    subtitleLines
  };
}

/**
 * Extract subtitle from full product name
 */
function getProductSubtitle(fullName: string): string | null {
  const parts = fullName.split(' - ');
  if (parts.length >= 3) {
    return parts.slice(2).join(' - ').trim();
  }
  if (parts.length === 2) {
    const secondPart = (parts[1] ?? '').trim();
    if (secondPart.length > 15 && !/^[A-Z0-9-]+$/.test(secondPart)) {
      return secondPart;
    }
  }
  return null;
}

/**
 * Get YouTube thumbnail URL from a video URL
 */
function getYoutubeThumbnail(url: string): string | null {
  const patterns = [/youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/, /youtu\.be\/([a-zA-Z0-9_-]+)/];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match?.[1]) {
      return `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`;
    }
  }
  return null;
}

/**
 * Pick the best single category for breadcrumbs (like Magento).
 * Magento shows the most specific product-type category, not the
 * broadest parent. We score candidates and pick the best one.
 */
function pickBreadcrumbCategory(categories: Category[]): Category | null {
  if (categories.length === 0) return null;
  if (categories.length === 1) return categories[0]!;

  // Score each category — lower is better
  const scored = categories.map(cat => {
    let score = 0;
    const len = cat.name.length;

    // Penalize very long names (content/knowledge-base articles)
    if (len > 50) score += 80;else if (len > 35) score += 40;

    // Penalize HTML entities (trademark symbols, etc.)
    if (/&trade;|&amp;|&ndash;|&reg;/i.test(cat.name)) score += 50;

    // Penalize content-style categories
    if (/compliance|authorization|knowledge|guide|picking|capturing|sanctions/i.test(cat.name)) score += 60;

    // Penalize very generic top-level categories (too broad for breadcrumbs)
    if (/^IP Camera$/i.test(cat.name)) score += 15;

    // Prefer product-type category names (cameras, NVR, etc.)
    if (/cameras?$/i.test(cat.name)) score -= 15;
    if (/^(IP|PoE|HD)\s/i.test(cat.name)) score -= 5;

    // Sweet spot: 10-30 chars is ideal for a breadcrumb
    if (len >= 10 && len <= 30) score -= 10;

    // Tiebreaker: moderate-length names preferred
    score += len * 0.3;
    return {
      cat,
      score
    };
  });
  scored.sort((a, b) => a.score - b.score);
  return scored[0]!.cat;
}
export function ProductPageContent(t0) {
  const $ = _c(145);
  const {
    product,
    tierPrices: tierPriceData,
    relatedProducts,
    optionProducts,
    detailSections
  } = t0;
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [adjustedPrice, setAdjustedPrice] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [lightboxItems, setLightboxItems] = useState(t1);
  let t2;
  if ($[1] !== product.sku) {
    t2 = [{
      sku: product.sku
    }];
    $[1] = product.sku;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const availability = useAvailability(t2);
  const availabilityState = availability.get(product.sku)?.state ?? "available";
  const partnerPricing = usePartnerPrice(product.slug);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = price => {
      setAdjustedPrice(price);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handlePriceChange = t3;
  let t4;
  if ($[4] !== product.slug) {
    t4 = [product.slug];
    $[4] = product.slug;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  useEffect(_temp, t4);
  let t5;
  if ($[6] !== product.images) {
    t5 = product.images.map(_temp2);
    $[6] = product.images;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const imageLightboxItems = t5;
  let t6;
  if ($[8] !== product.videos) {
    t6 = product.videos ?? [];
    $[8] = product.videos;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = t6.map(_temp3);
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const videoLightboxItems = t7;
  let t8;
  if ($[12] !== imageLightboxItems) {
    t8 = imageIndex => {
      setLightboxItems(imageLightboxItems);
      setLightboxIndex(imageIndex);
      setLightboxOpen(true);
    };
    $[12] = imageLightboxItems;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const openImageLightbox = t8;
  let t9;
  if ($[14] !== videoLightboxItems) {
    t9 = videoIndex => {
      setLightboxItems(videoLightboxItems);
      setLightboxIndex(videoIndex);
      setLightboxOpen(true);
    };
    $[14] = videoLightboxItems;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const openVideoLightbox = t9;
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = new Date();
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const now = t10;
  let t11;
  if ($[17] !== product.specialFrom) {
    t11 = product.specialFrom ? product.specialFrom instanceof Date ? product.specialFrom : new Date(product.specialFrom) : null;
    $[17] = product.specialFrom;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const specialFrom = t11;
  let t12;
  if ($[19] !== product.specialTo) {
    t12 = product.specialTo ? product.specialTo instanceof Date ? product.specialTo : new Date(product.specialTo) : null;
    $[19] = product.specialTo;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const specialTo = t12;
  const isSpecialActive = Boolean(product.specialPrice && (!specialFrom || now >= specialFrom) && (!specialTo || now <= specialTo));
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let videos;
  if ($[21] !== lightboxIndex || $[22] !== lightboxItems || $[23] !== lightboxOpen || $[24] !== product.categories || $[25] !== product.name || $[26] !== product.nameHtml || $[27] !== product.sku || $[28] !== product.videos) {
    const parsedNameHtml = product.nameHtml ? parseNameHtml(product.nameHtml) : null;
    const shortName = parsedNameHtml?.title ?? getShortProductName(product.name);
    const subtitleLines = parsedNameHtml ? parsedNameHtml.subtitleLines : (() => {
      const subtitle = getProductSubtitle(product.name);
      return subtitle ? [subtitle] : [];
    })();
    let t20;
    if ($[37] !== product.categories) {
      t20 = pickBreadcrumbCategory(product.categories);
      $[37] = product.categories;
      $[38] = t20;
    } else {
      t20 = $[38];
    }
    const breadcrumbCategory = t20;
    let t21;
    if ($[39] !== product.videos) {
      t21 = product.videos ?? [];
      $[39] = product.videos;
      $[40] = t21;
    } else {
      t21 = $[40];
    }
    videos = t21;
    t17 = "page-container";
    if ($[41] !== lightboxIndex || $[42] !== lightboxItems || $[43] !== lightboxOpen) {
      t18 = lightboxOpen && <ProductLightbox items={lightboxItems} initialIndex={lightboxIndex} onClose={() => setLightboxOpen(false)} />;
      $[41] = lightboxIndex;
      $[42] = lightboxItems;
      $[43] = lightboxOpen;
      $[44] = t18;
    } else {
      t18 = $[44];
    }
    let t22;
    if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <Link href="/">Home</Link>;
      $[45] = t22;
    } else {
      t22 = $[45];
    }
    let t23;
    if ($[46] !== breadcrumbCategory) {
      t23 = breadcrumbCategory && <span><span className="separator">{">"}</span><Link href={`/${breadcrumbCategory.slug}`}>{breadcrumbCategory.name}</Link></span>;
      $[46] = breadcrumbCategory;
      $[47] = t23;
    } else {
      t23 = $[47];
    }
    let t24;
    if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = <span className="separator">{">"}</span>;
      $[48] = t24;
    } else {
      t24 = $[48];
    }
    let t25;
    if ($[49] !== product.sku) {
      t25 = <span className="current">{product.sku}</span>;
      $[49] = product.sku;
      $[50] = t25;
    } else {
      t25 = $[50];
    }
    if ($[51] !== t23 || $[52] !== t25) {
      t19 = <nav className="product-breadcrumbs">{t22}{t23}{t24}{t25}</nav>;
      $[51] = t23;
      $[52] = t25;
      $[53] = t19;
    } else {
      t19 = $[53];
    }
    t16 = "product-layout";
    t13 = "product-info";
    t14 = <h1 className="product-title">{shortName}</h1>;
    t15 = subtitleLines.map(_temp4);
    $[21] = lightboxIndex;
    $[22] = lightboxItems;
    $[23] = lightboxOpen;
    $[24] = product.categories;
    $[25] = product.name;
    $[26] = product.nameHtml;
    $[27] = product.sku;
    $[28] = product.videos;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
    $[32] = t16;
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
    $[36] = videos;
  } else {
    t13 = $[29];
    t14 = $[30];
    t15 = $[31];
    t16 = $[32];
    t17 = $[33];
    t18 = $[34];
    t19 = $[35];
    videos = $[36];
  }
  const stock = getProductStockStatus(product, availabilityState);
  const t20 = `stock-badge ${stock.modifier}`;
  let t21;
  if ($[54] !== stock.label || $[55] !== t20) {
    t21 = <span className={t20}>{stock.label}</span>;
    $[54] = stock.label;
    $[55] = t20;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== stock.message) {
    t22 = <span className="stock-message">{stock.message}</span>;
    $[57] = stock.message;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] !== t21 || $[60] !== t22) {
    t23 = <div className="product-stock">{t21}{t22}</div>;
    $[59] = t21;
    $[60] = t22;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  const t24 = t23;
  let t25;
  if ($[62] !== adjustedPrice || $[63] !== isSpecialActive || $[64] !== partnerPricing || $[65] !== product.basePrice || $[66] !== product.specialPrice) {
    t25 = partnerPricing !== null ? (() => {
      const startingPrice = isSpecialActive && product.specialPrice ? parseFloat(product.specialPrice) : parseFloat(product.basePrice);
      const optionsDelta = adjustedPrice !== null ? adjustedPrice - startingPrice : 0;
      const retailShown = parseFloat(partnerPricing.retailPrice) + optionsDelta;
      const partnerShown = parseFloat(partnerPricing.partnerPrice) + optionsDelta;
      const money = _temp5;
      return <div className="product-price product-price-partner"><div className="retail-price-line">Retail Price: <span className="retail-amount">${money(retailShown)}</span></div><div className="partner-price-line">Partner Price: ${money(partnerShown)}</div></div>;
    })() : <div className="product-price">{adjustedPrice !== null ? <>${adjustedPrice.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}{isSpecialActive && product.specialPrice ? <span className="original-price">${parseFloat(product.basePrice).toLocaleString("en-US", {
            minimumFractionDigits: 2
          })}</span> : Math.abs(adjustedPrice - parseFloat(product.basePrice)) > 0.005 ? <span className="original-price">${parseFloat(product.basePrice).toLocaleString("en-US", {
            minimumFractionDigits: 2
          })}</span> : null}</> : isSpecialActive && product.specialPrice ? <>${parseFloat(product.specialPrice).toLocaleString("en-US", {
          minimumFractionDigits: 2
        })}<span className="original-price">${parseFloat(product.basePrice).toLocaleString("en-US", {
            minimumFractionDigits: 2
          })}</span></> : <>${parseFloat(product.basePrice).toLocaleString("en-US", {
          minimumFractionDigits: 2
        })}</>}</div>;
    $[62] = adjustedPrice;
    $[63] = isSpecialActive;
    $[64] = partnerPricing;
    $[65] = product.basePrice;
    $[66] = product.specialPrice;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== isSpecialActive || $[69] !== product.basePrice || $[70] !== product.specialPrice || $[71] !== tierPriceData) {
    t26 = tierPriceData && tierPriceData.length > 0 && <TierPriceTable tiers={tierPriceData} basePrice={isSpecialActive && product.specialPrice ? product.specialPrice : product.basePrice} />;
    $[68] = isSpecialActive;
    $[69] = product.basePrice;
    $[70] = product.specialPrice;
    $[71] = tierPriceData;
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  const t27 = product.id;
  const t28 = product.sku;
  const t29 = product.basePrice;
  const t30 = product.specialPrice;
  const t31 = product.msrp;
  const t32 = product.options;
  const t33 = Boolean(optionProducts && optionProducts.length > 0);
  const t34 = availabilityState === "unavailable";
  let t35;
  if ($[73] !== handlePriceChange || $[74] !== isSpecialActive || $[75] !== product.basePrice || $[76] !== product.id || $[77] !== product.msrp || $[78] !== product.options || $[79] !== product.sku || $[80] !== product.specialPrice || $[81] !== t33 || $[82] !== t34) {
    t35 = <ProductOptionsSelectorMagento productId={t27} baseSku={t28} basePrice={t29} specialPrice={t30} isSpecialActive={isSpecialActive} msrp={t31} options={t32} onPriceChange={handlePriceChange} showOptionsLink={t33} purchaseBlocked={t34} />;
    $[73] = handlePriceChange;
    $[74] = isSpecialActive;
    $[75] = product.basePrice;
    $[76] = product.id;
    $[77] = product.msrp;
    $[78] = product.options;
    $[79] = product.sku;
    $[80] = product.specialPrice;
    $[81] = t33;
    $[82] = t34;
    $[83] = t35;
  } else {
    t35 = $[83];
  }
  let t36;
  if ($[84] !== openImageLightbox || $[85] !== product.images || $[86] !== selectedImageIndex) {
    t36 = product.images.length > 0 && <div className="product-thumbnails-section"><div className="thumbnails-label">Images</div><div className="product-thumbnails">{product.images.map((image, index) => <button key={image.id} type="button" className={`product-thumbnail ${index === selectedImageIndex ? "selected" : ""}`} onClick={() => {
          setSelectedImageIndex(index);
          openImageLightbox(index);
        }}><Image src={image.url} alt={image.altText || ""} width={80} height={80} className="object-contain" /></button>)}</div></div>;
    $[84] = openImageLightbox;
    $[85] = product.images;
    $[86] = selectedImageIndex;
    $[87] = t36;
  } else {
    t36 = $[87];
  }
  let t37;
  if ($[88] !== openVideoLightbox || $[89] !== videos) {
    t37 = videos.length > 0 && <div className="product-videos-section"><div className="videos-label">Videos</div><div className="product-videos">{videos.map((video, index_0) => {
          const thumb = video.thumbnailUrl || getYoutubeThumbnail(video.url);
          return <button key={video.id} type="button" className="product-video-thumb" onClick={() => openVideoLightbox(index_0)} title={video.title || "Play video"}>{thumb && <Image src={thumb} alt={video.title || "Video thumbnail"} width={120} height={90} className="object-cover" />}<span className="play-icon"><svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M8 5v14l11-7z" /></svg></span></button>;
        })}</div></div>;
    $[88] = openVideoLightbox;
    $[89] = videos;
    $[90] = t37;
  } else {
    t37 = $[90];
  }
  let t38;
  if ($[91] !== t13 || $[92] !== t14 || $[93] !== t15 || $[94] !== t24 || $[95] !== t25 || $[96] !== t26 || $[97] !== t35 || $[98] !== t36 || $[99] !== t37) {
    t38 = <div className={t13}>{t14}{t15}{t24}{t25}{t26}{t35}{t36}{t37}</div>;
    $[91] = t13;
    $[92] = t14;
    $[93] = t15;
    $[94] = t24;
    $[95] = t25;
    $[96] = t26;
    $[97] = t35;
    $[98] = t36;
    $[99] = t37;
    $[100] = t38;
  } else {
    t38 = $[100];
  }
  let t39;
  if ($[101] !== openImageLightbox || $[102] !== product.mainVideo || $[103] !== selectedImageIndex) {
    t39 = product.mainVideo ? undefined : () => openImageLightbox(selectedImageIndex);
    $[101] = openImageLightbox;
    $[102] = product.mainVideo;
    $[103] = selectedImageIndex;
    $[104] = t39;
  } else {
    t39 = $[104];
  }
  const t40 = product.mainVideo ? undefined : "button";
  const t41 = product.mainVideo ? undefined : 0;
  let t42;
  if ($[105] !== openImageLightbox || $[106] !== product.mainVideo || $[107] !== selectedImageIndex) {
    t42 = product.mainVideo ? undefined : e => {
      if (e.key === "Enter") {
        openImageLightbox(selectedImageIndex);
      }
    };
    $[105] = openImageLightbox;
    $[106] = product.mainVideo;
    $[107] = selectedImageIndex;
    $[108] = t42;
  } else {
    t42 = $[108];
  }
  let t43;
  if ($[109] !== product.images || $[110] !== product.mainVideo || $[111] !== product.name || $[112] !== selectedImageIndex) {
    t43 = product.mainVideo ? <div className="product-gallery-main relative"><video className="product-spin-video" src={product.mainVideo} autoPlay={true} muted={true} loop={true} playsInline={true} preload="auto" /></div> : <ProductGallery images={product.images} productName={product.name} mainImageOnly={true} selectedImageIndex={selectedImageIndex} />;
    $[109] = product.images;
    $[110] = product.mainVideo;
    $[111] = product.name;
    $[112] = selectedImageIndex;
    $[113] = t43;
  } else {
    t43 = $[113];
  }
  let t44;
  if ($[114] !== t39 || $[115] !== t40 || $[116] !== t41 || $[117] !== t42 || $[118] !== t43) {
    t44 = <div className="product-gallery" onClick={t39} role={t40} tabIndex={t41} onKeyDown={t42}>{t43}</div>;
    $[114] = t39;
    $[115] = t40;
    $[116] = t41;
    $[117] = t42;
    $[118] = t43;
    $[119] = t44;
  } else {
    t44 = $[119];
  }
  let t45;
  if ($[120] !== t16 || $[121] !== t38 || $[122] !== t44) {
    t45 = <div className={t16}>{t38}{t44}</div>;
    $[120] = t16;
    $[121] = t38;
    $[122] = t44;
    $[123] = t45;
  } else {
    t45 = $[123];
  }
  let t46;
  if ($[124] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <InstallationCTA />;
    $[124] = t46;
  } else {
    t46 = $[124];
  }
  let t47;
  if ($[125] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <div className="product-details-header">Product Details</div>;
    $[125] = t47;
  } else {
    t47 = $[125];
  }
  let t48;
  if ($[126] !== detailSections) {
    t48 = detailSections.length > 0 && <ProductDetailsTabs sections={detailSections} />;
    $[126] = detailSections;
    $[127] = t48;
  } else {
    t48 = $[127];
  }
  let t49;
  if ($[128] !== product.weight) {
    t49 = product.weight && <p className="mt-4 text-sm text-muted-foreground">Weight: {product.weight} lbs</p>;
    $[128] = product.weight;
    $[129] = t49;
  } else {
    t49 = $[129];
  }
  let t50;
  if ($[130] !== t48 || $[131] !== t49) {
    t50 = <section className="product-details-card">{t47}<div className="p-4 sm:p-6">{t48}{t49}</div></section>;
    $[130] = t48;
    $[131] = t49;
    $[132] = t50;
  } else {
    t50 = $[132];
  }
  let t51;
  if ($[133] !== optionProducts) {
    t51 = optionProducts && optionProducts.length > 0 && <ProductOptionsVisual products={optionProducts} />;
    $[133] = optionProducts;
    $[134] = t51;
  } else {
    t51 = $[134];
  }
  let t52;
  if ($[135] !== relatedProducts) {
    t52 = relatedProducts && relatedProducts.length > 0 && <RelatedProducts products={relatedProducts} />;
    $[135] = relatedProducts;
    $[136] = t52;
  } else {
    t52 = $[136];
  }
  let t53;
  if ($[137] !== t17 || $[138] !== t18 || $[139] !== t19 || $[140] !== t45 || $[141] !== t50 || $[142] !== t51 || $[143] !== t52) {
    t53 = <div className={t17}>{t18}{t19}{t45}{t46}{t50}{t51}{t52}</div>;
    $[137] = t17;
    $[138] = t18;
    $[139] = t19;
    $[140] = t45;
    $[141] = t50;
    $[142] = t51;
    $[143] = t52;
    $[144] = t53;
  } else {
    t53 = $[144];
  }
  return t53;
}
function _temp5(n) {
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}
function _temp4(line, idx) {
  return <p key={idx} className="product-subtitle">{line}</p>;
}
function _temp3(vid) {
  return {
    type: "video" as const,
    url: vid.url,
    title: vid.title || undefined
  };
}
function _temp2(img) {
  return {
    type: "image" as const,
    url: img.url,
    title: img.altText || undefined
  };
}
function _temp() {
  window.scrollTo(0, 0);
}